import * as React from 'react';
import { observer } from 'mobx-react';
import { ManagerBudget } from 'nomst/Stores/Budget/managerBudget';
import { FormGroup, Input } from '@payscale/design';
import { navigateToNoMst } from 'utils/MobxDataManager/singletons';
import { cycleRules } from 'utils/MobxDataManager/singletons';
import { Configuration } from 'nomst/Stores/Configuration/configuration';

export interface IEditBudgetRowProps {
    useAutoSync: boolean;
    useProration: boolean;
    showOriginalBudget: boolean;
    showTotalCurrentPay: boolean;
    showAdjustedTargetPercentage: boolean;
    managerBudget: ManagerBudget;
    configuration: Configuration;
    onManagerBudgetPercentChangeCallbk?: () => void;
    onManagerBudgetValueChangeCallbk?: () => void;
}

export class EditBudgetRow extends React.Component<IEditBudgetRowProps> {
    constructor(props: IEditBudgetRowProps) {
        super(props);
        this.handleGoToManagerView = this.handleGoToManagerView.bind(this);
        this.handleChangeBudgetAmount = this.handleChangeBudgetAmount.bind(this);
        this.handleBlurBudgetAmount = this.handleBlurBudgetAmount.bind(this);
        this.handleChangeBudgetPercentage = this.handleChangeBudgetPercentage.bind(this);
        this.handleBlurBudgetPercentage = this.handleBlurBudgetPercentage.bind(this);
        this.handleBlurAdjustedBudgetInput = this.handleBlurAdjustedBudgetInput.bind(this);
        this.isManagerBudgetInputDisabled = this.isManagerBudgetInputDisabled.bind(this);
    }

    render(): React.ReactNode {
        const {
            managerBudget,
            useProration,
            showOriginalBudget,
            showAdjustedTargetPercentage,
            showTotalCurrentPay } = this.props;

        const managerName = cycleRules.isCycleClosedAndNotCurrent ?
            (
                <span>
                    {managerBudget.displayName}
                </span>
            ) :
            (
                <button className="pxl-link" onClick={this.handleGoToManagerView}>
                    {managerBudget.displayName}
                </button>
            );

        return (
            <tr>
                <td>
                    {managerName}
                </td>
                <td hidden={showTotalCurrentPay === false}>
                    <span data-mf-replace-inner="***">{managerBudget.employeesTotalCurrentPayText}</span>
                </td>
                <td hidden={showOriginalBudget === false}>
                    <span data-mf-replace-inner="***">{managerBudget.originalBudgetText}</span>
                </td>
                <td hidden={!useProration}>
                    <span data-mf-replace-inner="***">{managerBudget.adjustedBudgetBeforeProrationText}</span>
                </td>
                <td hidden={!useProration}>
                    <span data-mf-replace-inner="***">{managerBudget.proratedBudgetText}</span>
                </td>
                <td hidden={showAdjustedTargetPercentage === false}>
                    {this.renderAdjustedBudgetPercentage()}
                </td>
                <td>
                    {this.renderAdjustedBudget()}
                </td>
                <td>
                    <span data-mf-replace-inner="***">{managerBudget.spentText}</span>
                </td>
                <td>
                    <span data-mf-replace-inner="***" className={managerBudget.budgetBalanceClass}>
                        {managerBudget.budgetBalanceText}
                    </span>
                </td>
            </tr>
        );
    }

    private renderAdjustedBudgetPercentage(): React.ReactNode {
        const { managerBudget, useAutoSync } = this.props;
        if (useAutoSync) {
            return (
                <span data-mf-replace-inner="***">{managerBudget.adjustedBudgetPercentageText}</span>
            );
        }

        return (
            <FormGroup error={this.getErrorMessageText(managerBudget.adjustedBudgetPercentageInput)}>
                <Input
                    value={managerBudget.adjustedBudgetPercentageText}
                    disabled={this.isManagerBudgetInputDisabled()}
                    onChange={this.handleChangeBudgetPercentage}
                    onBlur={this.handleBlurBudgetPercentage}
                    className="increase-by-percentage"
                />
            </FormGroup>
        );
    }

    private isManagerBudgetInputDisabled(): boolean {
        const { managerBudget, configuration } = this.props;
        return managerBudget.employeesTotalCurrentPay === 0
        || configuration.isClosed
        || configuration.isClosing;
    }

    private renderAdjustedBudget(): React.ReactNode {
        const { managerBudget, useAutoSync } = this.props;
        if (useAutoSync) {
            return (
                <span data-mf-replace-inner="***">{managerBudget.adjustedBudgetText}</span>
            );
        }

        return (
            <FormGroup error={this.getErrorMessageText(managerBudget.adjustedBudgetInput)}>
                <Input
                    data-mf-replace="***"
                    value={managerBudget.adjustedBudgetText}
                    disabled={this.isManagerBudgetInputDisabled()}
                    onChange={this.handleChangeBudgetAmount}
                    onBlur={this.handleBlurAdjustedBudgetInput}
                    className="increase-by-amount"
                />
            </FormGroup>
        );
    }

    private handleGoToManagerView(): void {
        const { managerBudget } = this.props;
        navigateToNoMst.navigateToTeamsView(null, [managerBudget.employeeSourceId]);
    }

    private handleChangeBudgetAmount(_: undefined, value: string): void {
        if(this.props.onManagerBudgetValueChangeCallbk) {
            this.props.onManagerBudgetValueChangeCallbk();
        }
        this.props.managerBudget.changeBudgetValue(value);
    }

    private handleBlurBudgetAmount(): void {
        this.props.managerBudget.blurProratedBudgetInput();
    }

    private handleChangeBudgetPercentage(_: undefined, value: string): void {
        if(this.props.onManagerBudgetPercentChangeCallbk) {
            this.props.onManagerBudgetPercentChangeCallbk();
        }
        this.props.managerBudget.changeBudgetPercentage(value);
    }

    private handleBlurBudgetPercentage(): void {
        this.props.managerBudget.blurAdjustedBudgetPercentageInput();
    }

    private handleBlurAdjustedBudgetInput(): void {
        this.props.managerBudget.blurAdjustedBudgetInput();
    }

    private getErrorMessageText(input: Nullable<string>): string {
        const { managerBudget } = this.props;
        if (input && managerBudget.errorMessageText) {
            return managerBudget.errorMessageText;
        }

        return '';
    }
}

export default observer(EditBudgetRow);